import { QueryClient } from '@tanstack/react-query';
import { createTRPCReact, httpBatchLink, type CreateTRPCReact } from '@trpc/react-query';
import { TRPCClientError } from '@trpc/client';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import superjson from 'superjson';
import type { AppRouter } from '@marketingbee/api/router';
import { apiBase, getWorkspaceSlug } from './workspace';

/** Typed tRPC hooks: `trpc.workspace.current.useQuery()`, `trpc.crm.x.useMutation()`. */
export const trpc: CreateTRPCReact<AppRouter, unknown> = createTRPCReact<AppRouter>();

export type RouterInputs = inferRouterInputs<AppRouter>;
export type RouterOutputs = inferRouterOutputs<AppRouter>;

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        retry: (count, error) => {
          if (error instanceof TRPCClientError) {
            const code = (error.data as { code?: string } | undefined)?.code;
            if (code && ['UNAUTHORIZED', 'FORBIDDEN', 'NOT_FOUND', 'BAD_REQUEST'].includes(code)) return false;
          }
          return count < 2;
        },
      },
    },
  });
}

export function createTrpcClient() {
  return trpc.createClient({
    links: [
      httpBatchLink({
        url: `${apiBase}/trpc`,
        transformer: superjson,
        headers: () => {
          const slug = getWorkspaceSlug();
          return slug ? { 'x-workspace-slug': slug } : {};
        },
        fetch: (url, options) => fetch(url, { ...(options as RequestInit), credentials: 'include' }),
      }),
    ],
  });
}

/** Human-readable message for an error thrown by a tRPC call. */
export function errorMessage(error: unknown): string {
  if (error instanceof TRPCClientError) {
    const zodError = (error.data as { zodError?: { fieldErrors?: Record<string, string[]>; formErrors?: string[] } } | undefined)?.zodError;
    const first = zodError?.formErrors?.[0] ?? Object.values(zodError?.fieldErrors ?? {})[0]?.[0];
    return first ?? error.message;
  }
  if (error instanceof Error) return error.message;
  return 'Something went wrong';
}

export function errorCode(error: unknown): string | undefined {
  return error instanceof TRPCClientError ? (error.data as { code?: string } | undefined)?.code : undefined;
}
